import { useTranslation } from "react-i18next";
import { Body, Headline, Panel, Title } from "sunday-component";
import { SectionContainer } from "../style/AffiliatePage.style";
import {
  Collapse,
  FAQImage,
  FAQImageContainer,
  FAQPanel,
  FAQTabs,
  StyledList,
} from "./FAQSection.style";

type FAQSectionProps = {
  activeTab: string;
  setActiveTab: React.Dispatch<React.SetStateAction<string>>;
};
const FAQSection = ({ activeTab, setActiveTab }: FAQSectionProps) => {
  const { t, i18n } = useTranslation("affiliate");
  const locale = i18n.language;

  const faq = [
    {
      id: 1,
      tabName: t("faq.tab1.title"),
      children: {
        1: {
          question: {
            en: "How can I know if the people I referred have successfully purchased insurance and had their policies approved?",
            th: "จะรู้ได้อย่างไรว่าคนที่เราแนะนำไปมีการซื้อประกันสำเร็จและได้รับการอนุมัติแล้ว",
          },
          answer: {
            en: 'You can track your referral performance, such as the number of policies purchased, the amount under review, and the amount you will receive,  in "My performance" section. This information is updated daily at 15:00.\nRegarding policy approval status, Sunday cannot inform you (the referrer) about this because the company is not allowed to disclose the personal and health information of policyholders to third parties.',
            th: 'คุณสามารถตรวจสอบข้อมูลการแนะนำต่างๆ เช่น จำนวนกรมธรรม์ที่ซื้อแล้ว สถานะเงินรอตรวจสอบ และจำนวนเงินที่จะได้รับ ในหัวข้อ "ผลการแนะนำของฉัน" โดยข้อมูลนี้จะอัปเดตเวลา 15:00 น. ของทุกวัน\nสำหรับการอนุมัติกรมธรรม์ ซันเดย์จะไม่สามารถแจ้งเรื่องนี้ให้คุณ (ผู้แนะนำ) ทราบได้ เนื่องจากบริษัทไม่สามารถเปิดเผยข้อมูลส่วนตัวและข้อมูลสุขภาพของผู้เอากรมธรรม์กับบุคคลภายนอก',
          },
        },
        2: {
          question: {
            en: "When will I receive the referral fee after my referees have successfully purchased insurance and had their policies approved?",
            th: "หลังจากผู้ถูกแนะนำซื้อสำเร็จและกรมธรรม์ได้รับอนุมัติแล้ว จะได้รับค่าแนะนำเมื่อไหร่",
          },
          answer: {
            en: (
              <StyledList>
                <li>
                  Sunday calculates the referral fee on the 15th of each month
                  and pays them on the 25th.
                </li>
                <li>
                  To qualify, the referees’ policies must be approved and held
                  for at least 30 days. The 30th day of policy holding (the
                  &quot;Eligible Date&quot;) must fall on or before the 15th of
                  the month to receive the referral fee on the 25th of that
                  month.
                </li>
                <li>
                  Example 1: Policy approved on January 1st: Eligible Date is
                  January 30th. You will receive the referral fee on February
                  25th.
                </li>
                <li>
                  Example 2: Policy approved on January 20th: Eligible Date is
                  February 18th. You will receive the referral fee on March
                  25th, not February 25th, as it falls after the cutoff date
                  (February 15th).
                </li>
              </StyledList>
            ),
            th: (
              <StyledList>
                <li>
                  ซันเดย์จะตัดยอดทำการจ่ายค่าแนะนำในวันที่ 15 ของทุกเดือน
                  และจะทำการจ่ายค่าแนะนำในวันที่ 25 ของทุกเดือน
                </li>
                <li>
                  กรมธรรม์ของผู้ถูกแนะนำจะต้องได้รับการอนุมัติและถือครองอย่างน้อย
                  30 วัน โดยวันที่ 30 ของการถือกรมธรรม์ (ต่อไปนี้จะขอเรียกว่า
                  &quot;Eligible Date&quot;) จะต้องอยู่ภายในวันที่ 15
                  ของเดือนนั้นๆ จึงจะได้รับค่าแนะนำในวันที่ 25 ของเดือนนั้นๆ
                </li>
                <li>
                  ตัวอย่างที่ 1:
                  ผู้ถูกแนะนำซื้อประกันบนเว็บไซต์สำเร็จและได้รับการอนุมัติในวันที่
                  1 มกราคม จะถือว่าวันที่ 30 มกราคม เป็นวัน Eligible Date
                  ในกรณีนี้จะได้รับเงินค่าแนะนำในวันที่ 25 กุมภาพันธ์
                </li>
                <li>
                  ตัวอย่างที่ 2:
                  ผู้ถูกแนะนำซื้อประกันบนเว็บไซต์สำเร็จและได้รับการอนุมัติในวันที่
                  20 มกราคม จะถือว่าวันที่ 18 กุมภาพันธ์ เป็นวัน Eligible Date
                  ในกรณีนี้จะได้รับเงินค่าแนะนำในวันที่ 25 มีนาคม ไม่ใช่วันที่
                  25 กุมภาพันธ์ เพราะเลยวันตัดยอด (วันที่ 15 กุมภาพันธ์) มาแล้ว
                </li>
              </StyledList>
            ),
          },
        },
        3: {
          question: {
            en: "In which cases will I not receive the referral fee on the 25th of the month?",
            th: "กรณีใดบ้างที่ผู้แนะนำจะไม่ได้รับเงินในวันที่ 25 ของเดือน",
          },
          answer: {
            en: (
              <StyledList>
                <li>
                  In case your payout information is incomplete or incorrect,
                  the system will notify you to update it.
                  <br />
                  Once corrected, your referral fee will be processed in the
                  next payout cycle. Ensure your name on your bank account and
                  ID card match, and your address aligns with your ID card for
                  tax purposes.
                </li>
                <li>
                  In case the 25th falls on a non-business day, Sunday will
                  transfer the referral fee on the next business day.
                </li>
                <li>
                  In case the Eligible Date falls after the 15th of the month,
                  you will receive the referral fee on the 25th of the following
                  month.
                </li>
                <li>
                  In case the referee cancels the policy or the policy is
                  rejected by the insurance company.
                </li>
              </StyledList>
            ),
            th: (
              <StyledList>
                <li>
                  กรณีใส่ข้อมูลการรับเงินไม่ครบ หรือไม่ถูกต้อง
                  ซึ่งระบบจะแจ้งให้คุณอัปเดตข้อมูลให้เรียบร้อยก่อน
                  จึงจะสามารถทำการโอนเงินค่าแนะนำให้ได้ในรอบบัญชีถัดไป
                  โดยข้อมูลการรับเงินที่ถูกต้องจะต้องใช้ชื่อบัญชีธนาคารและบัตรประจำตัวประชาชนที่ตรงกัน
                  อีกทั้งข้อมูลที่อยู่จะต้องตรงกันกับในบัตรประจำตัวประชาชน
                  เนื่องจากต้องถูกระบุในใบหักภาษี ณ ที่จ่าย
                </li>
                <li>
                  กรณีวันที่ 25 ตรงกับวันหยุดทำการ
                  ซันเดย์จะทำการโอนจ่ายในวันทำการถัดไป
                </li>
                <li>
                  หาก Eligible Date เลยวันที่ 15 ของเดือน
                  คุณจะได้รับเงินค่าแนะนำในวันที่ 25 ของเดือนถัดไป
                </li>
                <li>
                  กรณีที่ผู้ถูกแนะนำทำการยกเลิกกรมธรรม์
                  หรือกรมธรรม์ของผู้ถูกแนะนำไม่ได้รับการอนุมัติ
                </li>
              </StyledList>
            ),
          },
        },
        4: {
          question: {
            en: "In which cases will I not receive the referral fee?",
            th: "กรณีใดบ้างที่ผู้แนะนำจะไม่ได้รับค่าแนะนำ",
          },
          answer: {
            en: (
              <>
                You will not receive a referral fee in any case that
                doesn&apos;t meet the terms and conditions of the Sunday
                Affiliate Program. Examples include:
                <StyledList>
                  <li>You (the referrer) are under 20 years old.</li>
                  <li>You are an insurance agent.</li>
                  <li>
                    The referred policy is rejected by the insurance company.
                  </li>
                  <li>
                    The referee cancels the policy within 30 days of approval.
                  </li>
                  <li>You make a purchase through your own referral link.</li>
                  <li>
                    The referred person is not a new customer (e.g., renewing an
                    existing policy).
                  </li>
                  <li>You post inappropriate referral content.</li>
                  <li>
                    The purchased product is not part of the eligible products.
                  </li>
                  <li>
                    The purchased product is less than THB 10,000 after
                    discounts.
                  </li>
                </StyledList>
              </>
            ),
            th: (
              <>
                ทุกกรณีที่ไม่เข้าข่ายเงื่อนไขและข้อตกลงของ Sunday Affiliate
                Program ยกตัวอย่าง เช่น
                <StyledList>
                  <li>มีอายุน้อยกว่า 20 ปี</li>
                  <li>เป็นตัวแทนประกันภัย</li>
                  <li>กรมธรรม์ของผู้ถูกแนะนำไม่ได้รับการอนุมัติ</li>
                  <li>ผู้ถูกแนะนำยกเลิกการซื้อใน 30 วัน</li>
                  <li>ทำการซื้อผ่านลิงก์แนะนำของตัวเอง</li>
                  <li>ผู้ถูกแนะนำไม่ใช่ลูกค้ารายใหม่ (ซื้อกรมธรรม์ปีต่อ)</li>
                  <li>โพสต์เนื้อหาการแนะนำที่ไม่เหมาะสม</li>
                  <li>ซื้อผลิตภัณฑ์ที่ไม่ได้เข้าร่วมรายการ</li>
                  <li>
                    ซื้อผลิตภัณฑ์ที่มีมูลค่าน้อยกว่า 10,000
                    บาทหลังจากหักส่วนลดใดๆ แล้ว
                  </li>
                </StyledList>
              </>
            ),
          },
        },
        5: {
          question: {
            en: "Where can I find the withholding tax certificate?",
            th: "ดูใบหักภาษี ณ ที่จ่ายได้ที่ไหน",
          },
          answer: {
            en: "For each referral payment, Sunday will withhold taxes as required by the Revenue Department. If you need a tax withholding certificate, please reach out to Sunday Customer Service at hello@easysunday.com or via LINE @easysunday.",
            th: "ในการโอนจ่ายค่าแนะนำทุกครั้ง ซันเดย์จะหักภาษี ณ ที่จ่ายตามที่กรมสรรพากรกำหนด หากต้องการใบเสร็จการหักภาษี ณ ที่จ่าย สามารถติดต่อ Sunday Customer Service ที่อีเมล hello@easysunday.com หรือ LINE @easysunday",
          },
        },
      },
    },
    {
      id: 2,
      tabName: t("faq.tab2.title"),
      children: {
        1: {
          question: {
            en: "How does Sunday calculate the referral fee based on the clicks from the referrer's link?",
            th: "ซันเดย์คำนวณการให้ค่าแนะนำจากการคลิกลิงก์ของผู้ถูกแนะนำอย่างไร",
          },
          answer: {
            en: "Your referees must make a purchase within 30 days of clicking your link. Otherwise, cookies will no longer store the referrer information on our website. Therefore, always remind referees to use your link when buying. If a referee clicks multiple referral links, credit will be given to the last link clicked.",
            th: "ผู้ถูกแนะนำต้องซื้อประกันบนเว็บไซต์ภายใน 30 วันหลังจากคลิกลิงก์ของคุณ หากเกิน 30 วัน ระบบคุกกี้ (Cookie) จะไม่เก็บข้อมูลโค้ดผู้แนะนำไว้บนเว็บไซต์ ฉะนั้นคุณอาจต้องแจ้งให้ผู้ถูกแนะนำใช้ลิงก์หรือโค้ดของคุณทุกครั้ง เมื่อทำการซื้อ ในกรณีที่ผู้ถูกแนะนำคลิกลิงก์ของผู้แนะนำหลายคน ระบบจะให้สิทธิ์แก่ลิงก์สุดท้ายที่ถูกคลิกก่อนชำระเงิน",
          },
        },
        2: {
          question: {
            en: "Can I change the URL in my referral link?",
            th: 'สามารถเปลี่ยนแปลง URL ส่วนหน้าของลิงก์ผู้แนะนำที่อยู่ในช่อง "ลิงก์ของคุณ" ได้หรือไม่',
          },
          answer: {
            en: (
              <>
                You can modify the URL part in your referral link, but ensure
                the URL you change is from retail.easysunday.com. However,
                changing the parameter after the question mark (as shown below)
                will prevent the system from calculating your referral fee.
                <FAQImageContainer>
                  <FAQImage
                    src="/static/pages/affiliatePage/FAQ-URLImage-en.png"
                    alt="FAQ-URLImage"
                  />
                </FAQImageContainer>
              </>
            ),
            th: (
              <>
                สามารถเปลี่ยนแปลงส่วนของ URL ได้และระบบจะยังนับค่าแนะนำให้
                แต่หากเปลี่ยนแปลงส่วน parameter และโค้ดผู้แนะนำ (ตามภาพด้านล่าง)
                ระบบจะไม่สามารถนับค่าแนะนำให้ได้ ทั้งนี้ URL
                ต้องเป็นลิงก์ที่อยู่บนเว็บไซต์ retail.easysunday.com เท่านั้น{" "}
                <br />
                <FAQImageContainer>
                  <FAQImage
                    src="/static/pages/affiliatePage/FAQ-URLImage-th.png"
                    alt="FAQ-URLImage"
                  />
                </FAQImageContainer>
              </>
            ),
          },
        },
        3: {
          question: {
            en: "Can referees use discount codes together with my referral link or code when purchasing insurance?",
            th: "ผู้ถูกแนะนำสามารถใช้โค้ดส่วนลดร่วมกับการใช้ลิงก์หรือโค้ดแนะนำของคุณ เพื่อซื้อประกันได้หรือไม่",
          },
          answer: {
            en: "Yes, discount codes can be used with referral links or codes. Sunday regularly offers special promotions each month. You can find current promotions on our website and share them with your referees.",
            th: "สามารถใช้ร่วมกันได้ โดยคุณสามารถเช็กโปรโมชันพิเศษที่ซันเดย์จัดเป็นประจำในแต่ละเดือนบนเว็บไซต์ เพื่อนำไปบอกต่อได้เลย",
          },
        },
        4: {
          question: {
            en: "If my referees have questions about policies and claims, what should they do?",
            th: "หากผู้ถูกแนะนำมีคำถามเกี่ยวกับกรมธรรม์และการเคลม ต้องทำอย่างไร",
          },
          answer: {
            en: "As a referrer, you are not authorized to answer questions about policies or claims since you are not a licensed insurance agent. Please advise your referees to review policy details on our website or contact Sunday Customer Service, available 24/7.",
            th: "คุณ (ผู้แนะนำ) จะไม่สามารถตอบคำถามเกี่ยวกับกรมธรรม์และการเคลมกับผู้ถูกแนะนำได้ เนื่องจากไม่ใช่ตัวแทนประกันที่มีใบอนุญาต แต่คุณสามารถแจ้งให้ผู้ถูกแนะนำอ่านข้อมูลกรมธรรม์บนเว็บไซต์ หรือติดต่อ Sunday Customer Service ได้ตลอด 24 ชั่วโมง",
          },
        },
        5: {
          question: {
            en: "How can I contact Sunday if I have questions about Sunday Affiliate Program?",
            th: "กรณีมีปัญหาเกี่ยวกับ Sunday Affiliate Program ต้องทำอย่างไร",
          },
          answer: {
            th: (
              <>
                สามารถติดต่อซันเดย์ผ่าน 3 ช่องทาง
                <StyledList>
                  <li>โทร 0 2026 3355</li>
                  <li>อีเมล hello@easysunday.com</li>
                  <li>LINE @easysunday</li>
                </StyledList>
              </>
            ),
            en: (
              <>
                You can contact Sunday through the following channels:
                <StyledList>
                  <li>Tel: +66 2026 3355</li>
                  <li>Email: hello@easysunday.com</li>
                  <li>LINE: @easysunday</li>
                </StyledList>
              </>
            ),
          },
        },
      },
    },
  ];

  return (
    <SectionContainer id="faq">
      <Headline level={6}>{t("faq.title")}</Headline>
      <FAQTabs activeKey={activeTab} onTabClick={setActiveTab}>
        {faq.map((item) => (
          <FAQPanel tab={<Title level={7}>{item.tabName}</Title>} key={item.id}>
            {Object.entries(item.children).map(([key, value]) => (
              <Collapse key={key}>
                <Panel
                  header={<Title level={7}>{value.question[locale]}</Title>}
                  key={key}
                >
                  <Body level={7}>{value.answer[locale]}</Body>
                </Panel>
              </Collapse>
            ))}
          </FAQPanel>
        ))}
      </FAQTabs>
    </SectionContainer>
  );
};

export default FAQSection;
